/**
 * extra.json background videos (W2-01a preview-gl; formats-and-packaging.md P§5.3, rendering.md R§8 step 3).
 *
 * Video preview is a P1 feature: it runs only when RenderOptions.videos is on (the preview panel badges videos
 * as "not previewed" otherwise). When on, each VideoDraw is drawn after the background and before the line bodies:
 *  - placement: the full-screen quad [-1, 1] x [-1/ar, 1/ar] in UI space (y down, image top at -1/ar), or under the
 *    attached line's own transform `m` (Phira's y-down quirk, computed by the engine); scale `cropCenter` covers
 *    (crops the overflow), `inside` letterboxes (outside stays untouched), `fit` stretches;
 *  - colour: rgb x (1 - dim) x tint.rgb, alpha x tint.a;
 *  - timing: shown while 0 <= localSec < the file's duration (Phira never loops videos, CR-W1-08-2);
 *  - frames: the <video> element follows the chart clock: while localSec advances at a steady rate the element plays
 *    (muted) at that rate and is re-seeked only when it drifts more than 0.25 s; otherwise it is paused and seeked
 *    to localSec. A finished seek asks the renderer for a redraw.
 */

import type { VideoDraw } from '../../core/engine/types';
import type { Mat2D } from '../../core/util/types';
import { colorByte } from '../gl/quadBatch';
import { coverUv } from './backgroundPass';
import { beginQuads, type PassContext } from './passContext';

/** Drift (s) tolerated while playing before the element is re-seeked. */
export const VIDEO_PLAY_DRIFT = 0.25;
/** Drift (s) tolerated while paused / scrubbing. */
export const VIDEO_PAUSED_DRIFT = 1 / 120;

/** Quad [x0, y0, x1, y1] and UV [u0, v0, u1, v1] of a video in UI space. */
export function videoQuad(
  scale: VideoDraw['scale'],
  ar: number,
  vw: number,
  vh: number,
): {
  readonly rect: readonly [number, number, number, number];
  readonly uv: readonly [number, number, number, number];
} {
  const h = 1 / ar;
  const full: readonly [number, number, number, number] = [-1, -h, 1, h];
  if (!(vw > 0 && vh > 0)) return { rect: full, uv: [0, 0, 1, 1] };
  if (scale === 'cropCenter') return { rect: full, uv: coverUv(vw, vh, 2, 2 * h) };
  if (scale === 'inside') {
    const va = vw / vh;
    if (va > ar) {
      const hh = 1 / va;
      return { rect: [-1, -hh, 1, hh], uv: [0, 0, 1, 1] };
    }
    const hw = h * va;
    return { rect: [-hw, -h, hw, h], uv: [0, 0, 1, 1] };
  }
  return { rect: full, uv: [0, 0, 1, 1] };
}

interface VideoState {
  lastLocal: number;
  lastWall: number;
  uploadedAt: number;
  seekedListener: boolean;
}

/** Keeps <video> elements in step with the chart clock. */
export class VideoSync {
  private readonly states = new Map<HTMLVideoElement, VideoState>();
  private readonly onFrame: () => void;
  private active = new Set<HTMLVideoElement>();
  private next = new Set<HTMLVideoElement>();

  constructor(onFrame: () => void) {
    this.onFrame = onFrame;
  }

  beginFrame(): void {
    this.next = new Set();
  }

  /** Pauses the elements not drawn this frame. */
  endFrame(): void {
    for (const v of this.active) if (!this.next.has(v) && !v.paused) v.pause();
    this.active = this.next;
  }

  private stateOf(v: HTMLVideoElement): VideoState {
    let s = this.states.get(v);
    if (s === undefined) {
      s = { lastLocal: NaN, lastWall: NaN, uploadedAt: NaN, seekedListener: false };
      this.states.set(v, s);
    }
    if (!s.seekedListener) {
      s.seekedListener = true;
      v.addEventListener('seeked', () => {
        this.onFrame();
      });
    }
    return s;
  }

  /** Brings `v` towards `localSec`; true when it has a frame to draw. */
  sync(v: HTMLVideoElement, localSec: number, nowMs: number): boolean {
    this.next.add(v);
    const s = this.stateOf(v);
    const dLocal = localSec - s.lastLocal;
    const dWall = (nowMs - s.lastWall) / 1000;
    s.lastLocal = localSec;
    s.lastWall = nowMs;
    const rate = dWall > 0 ? dLocal / dWall : NaN;
    const playing = dWall > 0 && dWall <= 0.25 && dLocal > 0 && rate >= 0.1 && rate <= 8;
    if (playing) {
      const r = Math.round(rate * 20) / 20;
      if (Math.abs(v.playbackRate - r) > 0.05) v.playbackRate = r;
      if (Math.abs(v.currentTime - localSec) > VIDEO_PLAY_DRIFT) v.currentTime = localSec;
      if (v.paused) {
        v.play().catch(() => undefined);
      }
    } else {
      if (!v.paused) v.pause();
      if (Math.abs(v.currentTime - localSec) > VIDEO_PAUSED_DRIFT && !v.seeking) v.currentTime = localSec;
    }
    return v.readyState >= 2;
  }

  /** Whether the element shows another frame than at its last upload (and remembers this one). */
  needsUpload(v: HTMLVideoElement): boolean {
    const s = this.stateOf(v);
    if (Object.is(s.uploadedAt, v.currentTime) && v.paused) return false;
    s.uploadedAt = v.currentTime;
    return true;
  }

  dispose(): void {
    for (const v of this.states.keys()) if (!v.paused) v.pause();
    this.states.clear();
    this.active.clear();
    this.next.clear();
  }
}

/** Draws the frame's videos; returns how many were drawn. */
export function drawVideos(ctx: PassContext, sync: VideoSync, enabled: boolean, nowMs: number): number {
  sync.beginFrame();
  let drawn = 0;
  if (enabled && ctx.provider !== null) {
    for (const d of ctx.list.videos) {
      if (!(d.localSec >= 0)) continue;
      const v = ctx.provider.video(d.asset);
      if (v === null) continue;
      const dur = v.duration;
      if (Number.isFinite(dur) && d.localSec >= dur) continue;
      if (!sync.sync(v, d.localSec, nowMs)) continue;
      const tex = sync.needsUpload(v) ? ctx.textures.refresh(v) : ctx.textures.get(v);
      if (tex === null) continue;
      const { rect, uv } = videoQuad(d.scale, ctx.vp.ar, v.videoWidth, v.videoHeight);
      const k = 1 - d.dim;
      const a = colorByte(d.alpha * d.tint[3]);
      if (a === 0) continue;
      const m: Mat2D = d.m ?? [1, 0, 0, 1, 0, 0];
      beginQuads(ctx, ctx.uiToClip, tex);
      ctx.batch.rect(
        m,
        rect[0],
        rect[1],
        rect[2],
        rect[3],
        uv[0],
        uv[1],
        uv[2],
        uv[3],
        colorByte(d.tint[0] * k),
        colorByte(d.tint[1] * k),
        colorByte(d.tint[2] * k),
        a,
      );
      drawn++;
    }
  }
  sync.endFrame();
  return drawn;
}
